import { Collaborator } from '../types';

// ----------------------------------------------------------------------

export const generateColor = (collaborators: Collaborator[]): string => {
  // Convert HSL to RGBA
  const hslToRGBA = (h: number, s: number, l: number, a: number = 0.7): string => {
    const hue = h / 360;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    const r = Math.round(hue2rgb(p, q, hue + 1 / 3) * 255);
    const g = Math.round(hue2rgb(p, q, hue) * 255);
    const b = Math.round(hue2rgb(p, q, hue - 1 / 3) * 255);

    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  // Predefined color palette with maximum contrast - 12 distinct colors
  // Colors are carefully selected with varying hue, saturation, and lightness
  // to ensure visual distinction even with many collaborators
  const colorPalette = [
    { hue: 0, saturation: 0.55, lightness: 0.82 },     // 1. Bright Pink/Red
    { hue: 30, saturation: 0.5, lightness: 0.88 },     // 2. Coral/Peach
    { hue: 60, saturation: 0.45, lightness: 0.90 },    // 3. Light Yellow
    { hue: 90, saturation: 0.50, lightness: 0.80 },    // 4. Lime Green
    { hue: 120, saturation: 0.40, lightness: 0.85 },   // 5. Mint Green
    { hue: 150, saturation: 0.45, lightness: 0.82 },   // 6. Seafoam
    { hue: 180, saturation: 0.42, lightness: 0.88 },   // 7. Light Cyan
    { hue: 210, saturation: 0.48, lightness: 0.83 },   // 8. Sky Blue
    { hue: 240, saturation: 0.45, lightness: 0.87 },   // 9. Periwinkle
    { hue: 270, saturation: 0.52, lightness: 0.84 },   // 10. Lavender/Purple
    { hue: 300, saturation: 0.50, lightness: 0.86 },   // 11. Orchid
    { hue: 330, saturation: 0.48, lightness: 0.85 },   // 12. Rose
  ];

  // Get existing colors' hues to avoid duplicates
  const existingColors = collaborators.map((c) => c.color);
  const usedHues: number[] = [];

  // Parse existing colors to extract hues
  existingColors.forEach((color) => {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1], 10) / 255;
      const g = parseInt(rgbaMatch[2], 10) / 255;
      const b = parseInt(rgbaMatch[3], 10) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;

      if (max !== min) {
        const d = max - min;

        switch (max) {
          case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
          case g:
            h = ((b - r) / d + 2) / 6;
            break;
          case b:
            h = ((r - g) / d + 4) / 6;
            break;
          default:
            h = 0;
            break;
        }
        h *= 360;
      }

      usedHues.push(Math.round(h));
    }
  });

  // Find the first unused color from palette
  for (let i = 0; i < colorPalette.length; i++) {
    const paletteColor = colorPalette[i];
    const isUsed = usedHues.some((usedHue) => Math.abs(usedHue - paletteColor.hue) < 30);

    if (!isUsed) {
      return hslToRGBA(paletteColor.hue, paletteColor.saturation, paletteColor.lightness, 0.7);
    }
  }

  // If all palette colors are used, generate a random pastel color as fallback
  const hue = Math.random() * 360;
  const saturation = 0.4 + Math.random() * 0.15;
  const lightness = 0.82 + Math.random() * 0.1;

  return hslToRGBA(hue, saturation, lightness, 0.7);
};
